/**
 * WhatsApp Helpers
 * Utility functions for WhatsApp chat ID normalization and formatting
 */

// ============ SENDER COLORS ============

/**
 * Palette colori per distinguere i sender nei gruppi
 * Colori Material Design con buon contrasto
 */
const SENDER_COLORS = [
  '#E91E63', // Pink
  '#9C27B0', // Purple
  '#673AB7', // Deep Purple
  '#3F51B5', // Indigo
  '#2196F3', // Blue
  '#00BCD4', // Cyan
  '#009688', // Teal
  '#4CAF50', // Green
  '#8BC34A', // Light Green
  '#FF9800', // Orange
  '#FF5722', // Deep Orange
  '#795548', // Brown
  '#607D8B', // Blue Grey
];

/**
 * Genera un colore consistente per un sender basato sul suo JID
 * Lo stesso JID produce sempre lo stesso colore
 * @param jid - JID del sender (es: "393123456789@c.us")
 * @returns Colore HEX (es: "#E91E63")
 */
export function getSenderColor(jid: string): string {
  if (!jid) return SENDER_COLORS[0];
  const hash = jid.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return SENDER_COLORS[hash % SENDER_COLORS.length];
}

/**
 * Formatta il nome del sender per visualizzazione
 * @param jid - JID del sender (può essere @c.us, @lid, etc.)
 * @param savedName - Nome salvato in rubrica (opzionale)
 * @returns Nome formattato
 */
export function formatSenderName(jid: string, savedName?: string): string {
  if (savedName) return savedName;
  if (!jid) return 'Sconosciuto';

  // Estrai numero da JID (es: "393123456789@c.us" o "172013608026319@lid")
  const phone = jid.split('@')[0];

  // Se il numero contiene solo cifre, formattalo come numero di telefono
  if (/^\d+$/.test(phone)) {
    // Formatta numero italiano con prefisso
    if (phone.startsWith('39') && phone.length >= 11) {
      return `+${phone.slice(0, 2)} ${phone.slice(2, 5)} ${phone.slice(5, 8)} ${phone.slice(8)}`;
    }

    // Numero internazionale generico
    return `+${phone}`;
  }

  // Se non è un numero (es: identificatori speciali), ritorna così com'è
  return phone || 'Sconosciuto';
}

// ============ CLIENT PHONE MATCHING ============

/**
 * Normalizza un numero di telefono rimuovendo tutto tranne le cifre
 */
function normalizePhone(phone: string | null | undefined): string {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
}

/**
 * Cerca un cliente il cui numero corrisponde al JID WhatsApp.
 * Cerca in: clients.phone, clients.whatsapp_number, client.contacts[].phone
 * Usa match con endsWith per gestire differenze di prefisso internazionale.
 * @returns Il cliente matchato o null
 */
export function findClientByPhone(
  chatJid: string,
  clients: { id: string; business_name: string; phone?: string; whatsapp_number?: string; contacts?: { phone?: string }[] }[]
): { id: string; business_name: string } | null {
  if (!chatJid || !clients?.length) return null;

  // Estrai numero dal JID (es. "393331234567@c.us" → "393331234567")
  const jidNumber = chatJid.split('@')[0].replace(/\D/g, '');
  if (!jidNumber || jidNumber.length < 7) return null;

  for (const client of clients) {
    // Match su clients.phone
    const clientPhone = normalizePhone(client.phone);
    if (clientPhone && (clientPhone.endsWith(jidNumber) || jidNumber.endsWith(clientPhone)) && clientPhone.length >= 7) {
      return { id: client.id, business_name: client.business_name };
    }

    // Match su clients.whatsapp_number
    const waNumber = normalizePhone(client.whatsapp_number);
    if (waNumber && (waNumber.endsWith(jidNumber) || jidNumber.endsWith(waNumber)) && waNumber.length >= 7) {
      return { id: client.id, business_name: client.business_name };
    }

    // Match su client_contacts[].phone
    if (client.contacts) {
      for (const contact of client.contacts) {
        const contactPhone = normalizePhone(contact.phone);
        if (contactPhone && (contactPhone.endsWith(jidNumber) || jidNumber.endsWith(contactPhone)) && contactPhone.length >= 7) {
          return { id: client.id, business_name: client.business_name };
        }
      }
    }
  }

  return null;
}

// ============ CHAT ID HELPERS ============

/**
 * Normalizza un chatId rimuovendo i suffissi WhatsApp per confronto
 * @param chatId - ID della chat (es: "393123456789@c.us" o "393123456789")
 * @returns ID normalizzato senza suffisso (es: "393123456789")
 */
export function normalizeChatId(chatId: string | null | undefined): string {
  if (!chatId) return '';
  // Rimuovi @c.us, @s.whatsapp.net, @g.us (gruppi), @lid (linked devices)
  return chatId
    .replace(/@c\.us$/, '')
    .replace(/@s\.whatsapp\.net$/, '')
    .replace(/@g\.us$/, '')
    .replace(/@lid$/, '');
}

/**
 * Assicura che un chatId abbia il suffisso completo @c.us
 * @param chatId - ID della chat
 * @returns ID con suffisso @c.us
 */
export function ensureFullChatId(chatId: string | null | undefined): string {
  if (!chatId) return '';
  // Se ha già un suffisso @, restituisci così com'è
  if (chatId.includes('@')) {
    return chatId;
  }
  // Altrimenti aggiungi @c.us (chat individuali)
  return `${chatId}@c.us`;
}

/**
 * Confronta due chatId ignorando differenze di formato
 * @param chatId1 - Primo ID
 * @param chatId2 - Secondo ID
 * @returns true se sono equivalenti
 */
export function chatIdsMatch(
  chatId1: string | null | undefined,
  chatId2: string | null | undefined
): boolean {
  return normalizeChatId(chatId1) === normalizeChatId(chatId2);
}
